import { useEffect, useMemo, useState } from "react";
import { api, apiUrl, fmtINR, fmtCompact, fmtPeriod, periodOptions } from "@/lib/api";
import { PageHeader, Section, Btn, Select, Input, Modal } from "@/components/Shared";
import { Download, ArrowUp, ArrowDown, ArrowUpDown, Info, Calculator } from "lucide-react";

export default function InternalMargin() {
  const [rows, setRows] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState("");
  const [q, setQ] = useState("");
  const [gatewayFilter, setGatewayFilter] = useState("");
  const [lobFilter, setLobFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [sortBy, setSortBy] = useState({ key: "net_revenue", dir: "desc" });

  const [partnerById, setPartnerById] = useState({});

  const load = async (p = period) => {
    const [calc, partners] = await Promise.all([
      api.get(`/calculations${p ? `?period=${p}` : ""}`),
      api.get("/partners"),
    ]);
    setRows((calc.data || []).filter((r) => !r.error));
    const uniqPeriods = [...new Set((calc.data || []).map((r) => r.period))].sort().reverse();
    if (uniqPeriods.length) setPeriods(uniqPeriods);
    setPartnerById(Object.fromEntries((partners.data || []).map((x) => [x.partner_id, x])));
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);
  const onPeriod = (v) => { setPeriod(v); load(v); };

  // Aggregate per merchant (sum across periods if no period filter).
  // Also keep the underlying calc rows + canonical rate fields so the
  // Net Commission popup can show the exact MDR / base / partner-share math.
  //
  // Secondary-tier handling (iter39.1): the calc engine emits a SEPARATE
  // row per secondary partner with `partner_tier="secondary"`, carrying
  // ONLY its own `partner_payout` (gross/volume/net are 0 by design so
  // they don't double-count against the primary row). If we naively summed
  // `net_commission`, the aggregate would be `(gross − primary_payout) + 0`
  // — missing the secondary payout subtraction. So on a secondary row we
  // subtract its `partner_payout` from `net_revenue` instead of adding
  // `net_commission`. Matches the pattern already used in the drill-down
  // popup at lines 354–358 below.
  const aggregated = useMemo(() => {
    const key = (r) => `${r.merchant_id}`;
    const map = {};
    for (const r of rows) {
      const k = key(r);
      if (!map[k]) {
        map[k] = {
          merchant_id: r.merchant_id,
          merchant_name: r.merchant_name,
          gateway: r.gateway,
          lob: r.lob,
          partner_id: r.referred_partner_id,
          partner_name: r.referred_partner_name,
          processed_volume: 0, gross_revenue: 0, partner_payout: 0, net_revenue: 0,
          // Latest-period rate snapshot for the popup (since rates are per-period)
          merchant_rate: r.merchant_rate || 0,
          base_rate: r.base_rate || 0,
          base_earnings: 0,
          merchant_billing: 0,
          commission_structure: r.commission_structure,
          partner_share_value: r.partner_share_value || 0,
          breakdown_rows: [],
        };
      }
      const isSecondary = r.partner_tier === "secondary";
      map[k].partner_payout += r.partner_payout || 0;
      if (isSecondary) {
        // Secondary rows only add an incremental payout on top of the
        // primary's already-computed net. Subtract that extra payout so
        // the row-level identity `net = gross − payout` holds.
        map[k].net_revenue -= r.partner_payout || 0;
      } else {
        map[k].processed_volume += r.processed_volume || 0;
        map[k].gross_revenue += r.gross_commission || 0;
        map[k].net_revenue += r.net_commission || 0;
        map[k].base_earnings += r.base_earnings || 0;
        map[k].merchant_billing += r.merchant_billing || 0;
        // Take the most-recent-period values for the rate snapshot (rates can
        // shift across periods; the popup labels make this clear).
        map[k].merchant_rate = r.merchant_rate || map[k].merchant_rate;
        map[k].base_rate = r.base_rate || map[k].base_rate;
        map[k].commission_structure = r.commission_structure || map[k].commission_structure;
        map[k].partner_share_value = r.partner_share_value || map[k].partner_share_value;
      }
      map[k].breakdown_rows.push(r);
    }
    return Object.values(map).map((r) => ({
      ...r,
      margin_pct: r.gross_revenue ? (r.net_revenue / r.gross_revenue) * 100 : 0,
    }));
  }, [rows]);

  const gatewayOpts = useMemo(() => [...new Set(aggregated.map((r) => r.gateway).filter(Boolean))].sort(), [aggregated]);
  const lobOpts = useMemo(() => [...new Set(aggregated.map((r) => r.lob).filter(Boolean))].sort(), [aggregated]);
  const partnerOpts = useMemo(() => {
    const seen = new Map();
    aggregated.forEach((r) => { if (r.partner_id && !seen.has(r.partner_id)) seen.set(r.partner_id, r.partner_name); });
    return [...seen.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([id, name]) => ({ value: id, label: `${id} · ${name}` }));
  }, [aggregated]);

  const filtered = useMemo(() => {
    let list = aggregated.filter((r) => {
      if (q && !`${r.merchant_id} ${r.merchant_name}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (gatewayFilter && r.gateway !== gatewayFilter) return false;
      if (lobFilter && r.lob !== lobFilter) return false;
      if (partnerFilter && r.partner_id !== partnerFilter) return false;
      return true;
    });
    const { key, dir } = sortBy;
    const mul = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = a[key], vb = b[key];
      if (typeof va === "number") return (va - vb) * mul;
      return String(va || "").localeCompare(String(vb || "")) * mul;
    });
    return list;
  }, [aggregated, q, gatewayFilter, lobFilter, partnerFilter, sortBy]);

  const totals = filtered.reduce((a, r) => ({
    vol: a.vol + r.processed_volume, gross: a.gross + r.gross_revenue,
    payout: a.payout + r.partner_payout, net: a.net + r.net_revenue,
  }), { vol: 0, gross: 0, payout: 0, net: 0 });
  const overallMargin = totals.gross ? (totals.net / totals.gross) * 100 : 0;

  // share of total net revenue for each merchant
  const netBase = totals.net || 1;
  const volBase = totals.vol || 1;

  const toggleSort = (key) => setSortBy((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });

  // Calc-explainer popup state
  const [explain, setExplain] = useState(null);  // null | aggregated row
  const SortHeader = ({ k, children, align = "left" }) => {
    const active = sortBy.key === k;
    const Icon = !active ? ArrowUpDown : (sortBy.dir === "asc" ? ArrowUp : ArrowDown);
    return (
      <th className={align === "right" ? "text-right" : ""}>
        <button type="button" data-testid={`im-sort-${k}`} onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-1 hover:text-[var(--ink)] ${active ? "text-[var(--ink)]" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}>
          <span>{children}</span>
          <Icon size={11} strokeWidth={2} className={active ? "text-[var(--accent)]" : "text-[var(--muted)]"} />
        </button>
      </th>
    );
  };

  const clearFilters = () => { setQ(""); setGatewayFilter(""); setLobFilter(""); setPartnerFilter(""); };
  const hasFilters = q || gatewayFilter || lobFilter || partnerFilter;

  return (
    <div data-testid="internal-margin-page">
      <PageHeader
        title="Internal Margin"
        actions={<>
          <div className="w-44"><Select testid="im-period" placeholder="All periods" options={periodOptions(periods)} value={period} onChange={onPeriod} /></div>
          <Btn variant="accent" testid="im-export" onClick={() => window.open(apiUrl(`/export/internal-margin${period ? `?period=${period}` : ""}`), "_blank")}><Download size={14} /> Export CSV</Btn>
        </>}
      />
      <div className="p-4 sm:p-10 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Gross Commission</div>
            <div className="num text-xl font-semibold mt-1.5">₹ {fmtCompact(totals.gross)}</div>
            <div className="text-[11px] text-[var(--muted)] mt-1">before partner payout</div>
          </div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Partner Payouts</div>
            <div className="num text-xl font-semibold mt-1.5 text-[var(--accent)]">₹ {fmtCompact(totals.payout)}</div>
          </div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4 ring-2 ring-[var(--positive)]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold flex items-center gap-1.5">
              Net Commission
              <span className="relative inline-flex group/tip" data-testid="im-net-info-wrap">
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[var(--muted)] hover:text-[var(--accent)] cursor-help" data-testid="im-net-info" aria-label="About Net Commission"><Info size={12} strokeWidth={2} /></span>
                <span role="tooltip" className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 z-20 w-64 px-3 py-2 bg-[var(--ink)] text-white text-[11px] font-normal normal-case tracking-normal leading-snug rounded-sm shadow-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150">= Gross Commission − Partner Payout. Operational &amp; other expenses are NOT included — see Summary or Monthly Accounting for BizAssist's Net Revenue.</span>
              </span>
            </div>
            <div className="num text-xl font-semibold mt-1.5 text-[var(--positive)]">₹ {fmtCompact(totals.net)}</div>
            <div className="text-[11px] text-[var(--muted)] mt-1">Gross − Partner Payout · before ops expenses</div>
          </div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Overall Margin</div>
            <div className="num text-xl font-semibold mt-1.5">{overallMargin.toFixed(1)}%</div>
            <div className="text-[11px] text-[var(--muted)] mt-1">Net Commission ÷ Gross Commission</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="im-filter-bar">
          <div className="w-56"><Input placeholder="Search merchant…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="im-search" /></div>
          <div className="w-40"><Select testid="im-gateway-filter" placeholder="All gateways" options={gatewayOpts} value={gatewayFilter} onChange={setGatewayFilter} /></div>
          <div className="w-44"><Select testid="im-lob-filter" placeholder="All LOBs" options={lobOpts} value={lobFilter} onChange={setLobFilter} /></div>
          <div className="w-56"><Select testid="im-partner-filter" placeholder="All partners" options={partnerOpts} value={partnerFilter} onChange={setPartnerFilter} /></div>
          {hasFilters && <Btn variant="ghost" testid="im-clear-filters" onClick={clearFilters}>Clear filters</Btn>}
        </div>

        <Section title={`Merchant-wise margin — ${filtered.length} merchant${filtered.length === 1 ? "" : "s"}${period ? ` · ${fmtPeriod(period)}` : " · all periods"}${hasFilters ? " · filtered" : ""}`} testid="im-table">
          <div className="overflow-auto max-h-[640px]">
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr>
                <SortHeader k="merchant_id">Merchant ID</SortHeader>
                <SortHeader k="merchant_name">Merchant</SortHeader>
                <SortHeader k="gateway">Gateway</SortHeader>
                <SortHeader k="lob">LOB</SortHeader>
                <SortHeader k="partner_name">Partner</SortHeader>
                <SortHeader k="processed_volume" align="right">Volume</SortHeader>
                <SortHeader k="gross_revenue" align="right">Gross Commission</SortHeader>
                <SortHeader k="partner_payout" align="right">Partner Payout</SortHeader>
                <SortHeader k="net_revenue" align="right">Net Commission</SortHeader>
                <th className="text-right">% of Net</th>
                <SortHeader k="margin_pct" align="right">Margin %</SortHeader>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.merchant_id} data-testid={`im-row-${r.merchant_id}`}>
                    <td className="num">{r.merchant_id}</td>
                    <td className="font-medium">{r.merchant_name}</td>
                    <td className="text-[12px]">{r.gateway || "—"}</td>
                    <td className="text-[12px]">{r.lob || "—"}</td>
                    <td className="text-[12px]">{r.partner_name || <span className="text-[var(--muted)]">—</span>}</td>
                    <td className="text-right num">₹ {fmtINR(r.processed_volume, 0)}</td>
                    <td className="text-right num">{fmtINR(r.gross_revenue, 0)}</td>
                    <td className="text-right num text-[var(--accent)]">{fmtINR(r.partner_payout, 0)}</td>
                    <td className="text-right num text-[var(--positive)] font-semibold">
                      <button
                        type="button"
                        onClick={() => setExplain(r)}
                        data-testid={`im-explain-${r.merchant_id}`}
                        className="inline-flex items-center gap-1 hover:underline focus:underline focus:outline-none"
                        title="Click for the full calculation breakdown"
                      >
                        {fmtINR(r.net_revenue, 0)}
                        <Calculator size={12} className="opacity-50" />
                      </button>
                    </td>
                    <td className="text-right num text-[12px] text-[var(--muted)]">{((r.net_revenue / netBase) * 100).toFixed(1)}%</td>
                    <td className="text-right num">{r.margin_pct.toFixed(1)}%</td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={11} className="text-center text-[var(--muted)] py-8">{hasFilters ? "No merchants match current filters." : "No calculation data yet. Add volumes to see margin."}</td></tr>}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-[var(--line-2)] bg-[var(--surface-2)] font-semibold text-[11.5px]">
                    <td colSpan={5} className="text-[10.5px] uppercase tracking-[0.15em] text-[var(--muted)]">Totals{hasFilters ? " (filtered)" : ""}</td>
                    <td className="text-right num">₹ {fmtINR(totals.vol, 0)}</td>
                    <td className="text-right num">{fmtINR(totals.gross, 0)}</td>
                    <td className="text-right num text-[var(--accent)]">{fmtINR(totals.payout, 0)}</td>
                    <td className="text-right num text-[var(--positive)]">{fmtINR(totals.net, 0)}</td>
                    <td className="text-right num text-[11.5px]">100%</td>
                    <td className="text-right num">{overallMargin.toFixed(1)}%</td>
                  </tr>
                </tfoot>
              )}
            </table></div>
          </div>
        </Section>

        <div className="text-[11px] text-[var(--muted)]">
          <span className="font-semibold">Volume share</span> of total: each row contributes {((100).toFixed(0))}% distributed across merchants — visible in the Summary page's Top Merchants & LOB tables.
          &nbsp;·&nbsp; <span className="font-semibold">Margin %</span> = Net Commission ÷ Gross Commission.
        </div>
      </div>
      {/* suppress unused-volBase lint */}
      <span className="hidden">{volBase}</span>

      {/* Net Commission calculation explainer */}
      <Modal open={!!explain} onClose={() => setExplain(null)} title={explain ? `Net Commission breakdown · ${explain.merchant_name}` : ""} testid="im-explain-modal" size="lg">
        {explain && (() => {
          const partner = partnerById[explain.partner_id] || {};
          const model = partner.compensation_model || "fixed_base_rate";
          const mdrBps = Math.round((explain.merchant_rate || 0) * 10000);
          const baseBps = Math.round((explain.base_rate || 0) * 10000);
          const partnerShareBps = Math.round((explain.partner_share_value || 0) * 10000);
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-[var(--surface-2)] border border-[var(--line)] p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold">Volume</div>
                  <div className="num text-base font-semibold mt-1">₹ {fmtINR(explain.processed_volume, 0)}</div>
                </div>
                <div className="bg-[var(--surface-2)] border border-[var(--line)] p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold">MDR (Merchant rate)</div>
                  <div className="num text-base font-semibold mt-1"><span className="text-[var(--ink)]">{mdrBps} bps</span> <span className="text-[11px] text-[var(--muted)]">({(explain.merchant_rate * 100).toFixed(4).replace(/\.?0+$/, "")}%)</span></div>
                </div>
                <div className="bg-[var(--surface-2)] border border-[var(--line)] p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold">PG Base rate</div>
                  <div className="num text-base font-semibold mt-1"><span className="text-[var(--ink)]">{baseBps} bps</span> <span className="text-[11px] text-[var(--muted)]">({(explain.base_rate * 100).toFixed(4).replace(/\.?0+$/, "")}%)</span></div>
                </div>
                <div className="bg-[var(--surface-2)] border border-[var(--line)] p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] font-semibold">Partner</div>
                  <div className="text-[12px] font-medium mt-1">{explain.partner_name || "—"}</div>
                  <div className="text-[10px] text-[var(--muted)] uppercase tracking-wider mt-0.5">{model.replace("_", " ")}</div>
                </div>
              </div>

              <Section title="Step-by-step calculation" testid="im-explain-steps">
                <div className="overflow-x-auto"><table className="table-data w-full">
                  <thead><tr>
                    <th>Step</th>
                    <th className="text-right">Formula</th>
                    <th className="text-right">Amount (₹)</th>
                  </tr></thead>
                  <tbody>
                    <tr>
                      <td><b>1.</b> Merchant billing</td>
                      <td className="text-right num text-[12px] text-[var(--muted)]">Volume × MDR <span className="text-[10px]">({mdrBps} bps)</span></td>
                      <td className="text-right num">₹ {fmtINR(explain.merchant_billing, 0)}</td>
                    </tr>
                    <tr>
                      <td><b>2.</b> PG cost</td>
                      <td className="text-right num text-[12px] text-[var(--muted)]">Volume × Base <span className="text-[10px]">({baseBps} bps)</span></td>
                      <td className="text-right num text-[var(--negative)]">− ₹ {fmtINR(explain.base_earnings, 0)}</td>
                    </tr>
                    <tr className="font-semibold border-t-2 border-[var(--line-2)] bg-[var(--surface-2)]">
                      <td><b>3.</b> Gross Commission</td>
                      <td className="text-right num text-[11.5px] text-[var(--muted)]">= billing − base = <span className="num">{Math.round(((explain.merchant_rate - explain.base_rate) || 0) * 10000)}</span> bps × volume</td>
                      <td className="text-right num">₹ {fmtINR(explain.gross_revenue, 0)}</td>
                    </tr>
                    <tr>
                      <td><b>4.</b> Partner Payout</td>
                      <td className="text-right num text-[12px] text-[var(--muted)]">
                        {model === "revenue_share"
                          ? <>Gross × {(explain.partner_share_value * 100).toFixed(2)}% (rev share)</>
                          : model === "fixed_fee"
                          ? <>Flat fee · not volume-based</>
                          : <>Volume × <span className="num">{partnerShareBps}</span> bps (MDR − Partner base)</>}
                      </td>
                      <td className="text-right num text-[var(--accent)]">− ₹ {fmtINR(explain.partner_payout, 0)}</td>
                    </tr>
                    <tr className="font-bold border-t-2 border-[var(--ink-2)]">
                      <td><b>5.</b> Net Commission</td>
                      <td className="text-right num text-[11.5px] text-[var(--muted)]">= gross − partner payout · margin <span className="num">{explain.margin_pct.toFixed(1)}%</span></td>
                      <td className="text-right num text-[var(--positive)]">₹ {fmtINR(explain.net_revenue, 0)}</td>
                    </tr>
                  </tbody>
                </table></div>
              </Section>

              {explain.breakdown_rows.length > 1 && (() => {
                // Iter31.5 — collapse primary + secondary tier rows for the
                // same (period, effective_date) into ONE display line.
                // Without this, secondary tier rows surface as a duplicate
                // period row with 0s for MDR/base/gross/net (those fields
                // belong to the primary tier — secondary is "on top of"
                // the primary contribution and doesn't carry its own
                // billing context).
                const periodGroups = new Map();
                for (const br of explain.breakdown_rows) {
                  const eff = br.effective_date || br.rate_anchor_date || "";
                  const k = `${br.period}|${eff}`;
                  if (!periodGroups.has(k)) {
                    periodGroups.set(k, {
                      period: br.period,
                      effective_date: br.effective_date || "",
                      rate_anchor_date: br.rate_anchor_date || "",
                      processed_volume: 0,
                      merchant_rate: 0,
                      base_rate: 0,
                      gross_commission: 0,
                      partner_payout: 0,
                      net_commission: 0,
                    });
                  }
                  const g = periodGroups.get(k);
                  // Primary tier carries volume + rates + gross + net.
                  // Secondary tier carries only an additional partner_payout
                  // (and can be a different partner; its volume/rates/gross/net
                  // are all 0 by design — see calc engine line 1161).
                  if (br.partner_tier === "secondary") {
                    g.partner_payout += br.partner_payout || 0;
                    // Net is gross − total_partner_payouts; subtract the secondary
                    // contribution from the primary's already-included net.
                    g.net_commission -= br.partner_payout || 0;
                  } else {
                    g.processed_volume += br.processed_volume || 0;
                    g.merchant_rate = br.merchant_rate || g.merchant_rate;
                    g.base_rate = br.base_rate || g.base_rate;
                    g.gross_commission += br.gross_commission || 0;
                    g.partner_payout += br.partner_payout || 0;
                    g.net_commission += br.net_commission || 0;
                  }
                }
                const collapsed = [...periodGroups.values()].sort((a, b) => {
                  const k = (x) => `${x.period}|${x.effective_date || x.rate_anchor_date || ""}`;
                  return k(a).localeCompare(k(b));
                });
                if (collapsed.length <= 1) return null;
                return (
                  <Section title={`Period-by-period breakdown · ${collapsed.length} entr${collapsed.length === 1 ? "y" : "ies"}`} testid="im-explain-by-period">
                    <div className="overflow-x-auto"><table className="table-data w-full">
                      <thead><tr>
                        <th>Period</th>
                        <th>Effective Date</th>
                        <th className="text-right">Volume</th>
                        <th className="text-right">MDR</th>
                        <th className="text-right">Base</th>
                        <th className="text-right">Gross</th>
                        <th className="text-right">Payout</th>
                        <th className="text-right">Net</th>
                      </tr></thead>
                      <tbody>
                        {collapsed.map((br) => (
                          <tr key={`${br.period}|${br.effective_date || br.rate_anchor_date || ""}`}>
                            <td className="num font-medium">{fmtPeriod(br.period)}</td>
                            <td className="num text-[12px]" title={br.effective_date ? `Volume effective from ${br.effective_date}` : (br.rate_anchor_date ? `Anchored to period end: ${br.rate_anchor_date}` : "")}>
                              {br.effective_date || <span className="text-[var(--muted)]">—</span>}
                            </td>
                            <td className="text-right num">₹ {fmtCompact(br.processed_volume)}</td>
                            <td className="text-right num text-[12px]">{Math.round((br.merchant_rate || 0) * 10000)} bps</td>
                            <td className="text-right num text-[12px]">{Math.round((br.base_rate || 0) * 10000)} bps</td>
                            <td className="text-right num">₹ {fmtINR(br.gross_commission, 0)}</td>
                            <td className="text-right num text-[var(--accent)]">₹ {fmtINR(br.partner_payout, 0)}</td>
                            <td className="text-right num text-[var(--positive)] font-semibold">₹ {fmtINR(br.net_commission, 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  </Section>
                );
              })()}
            </div>
          );
        })()}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="ghost" onClick={() => setExplain(null)}>Close</Btn>
        </div>
      </Modal>
    </div>
  );
}
